import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export default function FAQ() {
  const [expanded, setExpanded] = React.useState([]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(
      isExpanded ? [...expanded, panel] : expanded.filter((item) => item !== panel),
    );
  };

  return (
    <Container
      id="faq"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 },
      }}
    >
      <Typography
        component="h2"
        variant="h4"
        sx={{
          color: 'text.primary',
          width: { sm: '100%', md: '60%' },
          textAlign: { sm: 'left', md: 'center' },
          animation: `${fadeInUp} 1s ease-out`,
        }}
      >
        Preguntas frecuentes
      </Typography>
      <Box sx={{ width: '100%' }}>
        <Accordion
          expanded={expanded.includes('panel1')}
          onChange={handleChange('panel1')}
          sx={{
            mb: 2,
            animation: `${slideIn} 0.6s ease-out 0.2s both`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(85, 180, 172, 0.1)',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1d-content"
            id="panel1d-header"
            sx={{
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: '#55b4ac',
                transition: 'transform 0.3s ease',
              },
              '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                transform: 'rotate(180deg)',
              },
            }}
          >
            <Typography component="span" variant="subtitle2">
              ¿Cómo puedo contactar al soporte técnico si tengo una pregunta o problema?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              Puedes contactar a nuestro equipo de soporte técnico enviando un email a&nbsp;
              <Link href="mailto:soporte@bankivo.com" sx={{ color: '#55b4ac' }}>
                soporte@bankivo.com
              </Link>
              &nbsp;o llamando a nuestro número gratuito. Estamos aquí para asistirte
              de manera rápida y eficiente.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('panel2')}
          onChange={handleChange('panel2')}
          sx={{
            mb: 2,
            animation: `${slideIn} 0.6s ease-out 0.4s both`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(85, 180, 172, 0.1)',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2d-content"
            id="panel2d-header"
            sx={{
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: '#55b4ac',
                transition: 'transform 0.3s ease',
              },
              '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                transform: 'rotate(180deg)',
              },
            }}
          >
            <Typography component="span" variant="subtitle2">
              ¿Puedo cancelar mi suscripción si no cumple con mis expectativas?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              ¡Por supuesto! Ofrecemos una política de cancelación sin complicaciones. Si no estás
              completamente satisfecho con el servicio, puedes cancelar tu suscripción en cualquier
              momento sin penalizaciones. Tu acceso continuará hasta el final del período de facturación actual.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('panel3')}
          onChange={handleChange('panel3')}
          sx={{
            mb: 2,
            animation: `${slideIn} 0.6s ease-out 0.6s both`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(85, 180, 172, 0.1)',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3d-content"
            id="panel3d-header"
            sx={{
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: '#55b4ac',
                transition: 'transform 0.3s ease',
              },
              '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                transform: 'rotate(180deg)',
              },
            }}
          >
            <Typography component="span" variant="subtitle2">
              ¿Qué hace que Bankivo se destaque de otras plataformas de gestión de préstamos?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              Bankivo se distingue por su enfoque integral en la gestión crediticia, ofreciendo herramientas
              avanzadas de análisis de riesgo, automatización de cobranza y reportes en tiempo real. Nuestra
              plataforma está diseñada específicamente para empresas financieras, con escalabilidad y
              personalización según las necesidades de cada institución.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('panel4')}
          onChange={handleChange('panel4')}
          sx={{
            mb: 2,
            animation: `${slideIn} 0.6s ease-out 0.8s both`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(85, 180, 172, 0.1)',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel4d-content"
            id="panel4d-header"
            sx={{
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: '#55b4ac',
                transition: 'transform 0.3s ease',
              },
              '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                transform: 'rotate(180deg)',
              },
            }}
          >
            <Typography component="span" variant="subtitle2">
              ¿Qué garantías de seguridad y respaldo de datos ofrece Bankivo?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              Bankivo garantiza la máxima seguridad de tus datos con encriptación de extremo a extremo,
              respaldos automáticos diarios y cumplimiento de estándares internacionales de seguridad.
              Ofrecemos un SLA del 99.9% de disponibilidad y soporte técnico 24/7 para asegurar la
              continuidad de tu operación crediticia.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('panel5')}
          onChange={handleChange('panel5')}
          sx={{
            mb: 2,
            animation: `${slideIn} 0.6s ease-out 1.0s both`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(85, 180, 172, 0.1)',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel5d-content"
            id="panel5d-header"
            sx={{
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: '#55b4ac',
                transition: 'transform 0.3s ease',
              },
              '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                transform: 'rotate(180deg)',
              },
            }}
          >
            <Typography component="span" variant="subtitle2">
              ¿Cómo funciona la integración con sistemas bancarios y contables existentes?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              Bankivo ofrece APIs robustas y conectores pre-construidos para integrarse fácilmente con
              sistemas bancarios, contables y de gestión existentes. Nuestro equipo técnico te ayuda
              con la implementación y configuración para asegurar una transición sin interrupciones
              en tu operación actual.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('panel6')}
          onChange={handleChange('panel6')}
          sx={{
            mb: 2,
            animation: `${slideIn} 0.6s ease-out 1.2s both`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(85, 180, 172, 0.1)',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel6d-content"
            id="panel6d-header"
            sx={{
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: '#55b4ac',
                transition: 'transform 0.3s ease',
              },
              '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                transform: 'rotate(180deg)',
              },
            }}
          >
            <Typography component="span" variant="subtitle2">
              ¿Qué tipos de reportes y análisis puedo generar con Bankivo?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              Bankivo incluye más de 50 reportes pre-configurados incluyendo análisis de cartera,
              reportes de mora, flujo de caja, rentabilidad por producto, y métricas de riesgo.
              También puedes crear reportes personalizados con nuestro constructor visual y
              programar envíos automáticos por email.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Container>
  );
}
